import { Button, Tooltip } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { actions } from 'components/conversation/actions/conversation';
import { getConversationCloseButtonMode } from 'components/conversation/utils/conversationCloseButton';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

const { conversationClose } = actions;

export const ConversationCloseButton = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const hasReplyAccess = useHasAccess('conversation.reply');
  const { conversation, isAssignedToMe, asyncStatus } = useAppSelector(
    (state) => ({
      conversation: state.conversation.metaConversation.conversation,
      isAssignedToMe: state.conversation.metaConversation.isAssignedToMe,
      asyncStatus: state.conversation.asyncStatus,
    }),
  );

  const {
    is_closed: isClosed,
    id: csid,
    status,
    initial_channel: initialChannel,
    live_assesment: liveAssessment,
  } = conversation;

  const isDisabled = isClosed || !isAssignedToMe || csid === null;

  const closeConversation = async () => {
    try {
      await bindActionToPromise(
        dispatch,
        conversationClose,
      )({
        csid,
      });

      ampli.conversationClosed({
        location: 'conversation',
        channel: conversation.initial_channel || undefined,
        language: conversation.attributes?.language,
        direction: conversation.direction || undefined,
        tags: conversation.tags,
      });
    } catch (exception) {
      console.error('conversation close exception', exception);
    }
  };

  const mode = getConversationCloseButtonMode(
    status,
    initialChannel,
    liveAssessment?.is_end_user_connected,
  );

  const titles =
    mode === 'close'
      ? {
          tooltip: 'Close conversation',
          loading: 'Closing...',
          button: isClosed ? 'Closed' : 'Close',
        }
      : {
          tooltip: 'End conversation',
          loading: 'Ending...',
          button: 'End conversation',
        };

  const tooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:close">
      {titles.tooltip}
    </CommandKeybindingsTooltipContent>
  );

  return (
    <Tooltip
      content={
        hasReplyAccess
          ? tooltipContent
          : "You don't have permission to close conversation"
      }
      dark
      disabled={isDisabled}
    >
      <Button
        variant="secondary"
        disabled={!hasReplyAccess || isDisabled}
        isLoading={asyncStatus.close === 'loading'}
        loadingIndicator={titles.loading}
        onClick={closeConversation}
      >
        {titles.button}
      </Button>
    </Tooltip>
  );
};
